import React from 'react';
import styled from 'styled-components';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { ChartCard } from './ChartCard';
import { colors } from '../../theme/tokens';
import { DEMO_COMPETITOR_COMPARISON } from './demoData';

const DEMO_DATA = DEMO_COMPETITOR_COMPARISON;

export const CompetitorComparisonCard: React.FC = () => {
  const clientData = DEMO_DATA[0];
  const sortedByArticles = [...DEMO_DATA].sort(
    (a, b) => b.Articles - a.Articles
  );
  const sortedBySources = [...DEMO_DATA].sort((a, b) => b.Sources - a.Sources);
  const articleRank =
    sortedByArticles.findIndex((d) => d.name === clientData.name) + 1;
  const sourceRank =
    sortedBySources.findIndex((d) => d.name === clientData.name) + 1;

  return (
    <ChartCard
      title="Competitor Comparison (2026)"
      subtitle="Chaque barre montre les articles et les sources"
    >
      <BadgeRow>
        <RankBadge $color={colors.indigo}>
          Article Rank: #{articleRank}
        </RankBadge>
        <RankBadge $color={colors.olive}>Source Rank: #{sourceRank}</RankBadge>
      </BadgeRow>

      <ChartWrap>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={DEMO_DATA}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: colors.inkMuted }}
            />
            <YAxis width={40} tick={{ fontSize: 11, fill: colors.inkMuted }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="Articles" fill={colors.indigo} />
            <Bar dataKey="Sources" fill={colors.olive} />
          </BarChart>
        </ResponsiveContainer>
      </ChartWrap>

      <SummaryBox>
        <SummaryTitle>{clientData.name} Performance</SummaryTitle>
        <SummaryRow>
          <SummaryStat>
            <span style={{ color: colors.indigo, fontWeight: 600 }}>
              Articles:
            </span>{' '}
            {clientData.Articles}
          </SummaryStat>
          <SummaryStat>
            <span style={{ color: colors.olive, fontWeight: 600 }}>
              Sources:
            </span>{' '}
            {clientData.Sources}
          </SummaryStat>
        </SummaryRow>
      </SummaryBox>
    </ChartCard>
  );
};

// --- Styled ---

const BadgeRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const RankBadge = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 600;
  color: var(--white);
  background: ${(p) => p.$color};
  border-radius: var(--radius-pill);
`;

const ChartWrap = styled.div`
  margin-top: 8px;
`;

const SummaryBox = styled.div`
  margin-top: 16px;
  padding: 12px 16px;
  background: ${colors.indigoSoft};
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
`;

const SummaryTitle = styled.p`
  font-family: var(--font-sans-semibold, var(--font-sans));
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  margin: 0 0 6px;
`;

const SummaryRow = styled.div`
  display: flex;
  gap: 16px;
`;

const SummaryStat = styled.span`
  font-family: var(--font-sans);
  font-size: 13px;
  color: var(--ink-muted);
`;
